import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import PublicLayout from '../components/public/PublicLayout';
import { PageHero, EmptyState } from './UniversitiesListPage';
import { Globe2, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Flag emoji map (most common study destinations)
const FLAGS = {
  'United Kingdom': '🇬🇧',
  'Canada': '🇨🇦',
  'Australia': '🇦🇺',
  'United States': '🇺🇸',
  'Ireland': '🇮🇪',
  'Germany': '🇩🇪',
  'Netherlands': '🇳🇱',
  'New Zealand': '🇳🇿',
  'Malta': '🇲🇹',
  'UAE': '🇦🇪',
};

const CountriesListPage = () => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/public/master-data?type=country`)
      .then((r) => setCountries(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCountries([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PublicLayout>
      <PageHero title="Find by Countries" subtitle="Pick a destination — explore universities, courses, and visa pathways in each country." />

      <section className="max-w-7xl mx-auto px-4 py-12">
        {loading ? (
          <p className="text-center text-gray-500">Loading countries…</p>
        ) : countries.length === 0 ? (
          <EmptyState label="No countries available yet." />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="country-grid">
            {countries.map((c) => (
              <Link
                key={c.id || c.name}
                to={`/universities?country=${encodeURIComponent(c.name)}`}
                className="group bg-white border border-gray-100 rounded-xl p-5 text-center hover:border-[#F5A623] hover:shadow-lg transition"
                data-testid={`country-card-${c.id || c.name}`}
              >
                <div className="text-5xl mb-3">{FLAGS[c.name] || '🌍'}</div>
                <h3 className="font-bold text-[#1A2A4F] text-sm">{c.name}</h3>
                <span className="mt-2 inline-flex items-center gap-1 text-xs text-[#F5A623] font-semibold opacity-0 group-hover:opacity-100 transition">
                  Explore <ChevronRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 pb-16">
        <div className="bg-[#1A2A4F] text-white rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="flex items-start gap-4">
            <Globe2 className="w-10 h-10 text-[#F5A623] shrink-0" />
            <div>
              <h3 className="text-xl font-bold">Need help choosing a country?</h3>
              <p className="text-sm opacity-90 mt-1">Our counsellors can match you to the destination that fits your budget, subject, and visa profile.</p>
            </div>
          </div>
          <Link to="/contact" className="bg-[#F5A623] text-white font-semibold px-6 py-3 rounded-md hover:brightness-95 transition" data-testid="countries-cta-contact">Talk to a counsellor</Link>
        </div>
      </section>
    </PublicLayout>
  );
};

export default CountriesListPage;
